import React from 'react';
import { JiraFieldData2, JiraSprintLiteData, JiraUserLiteData } from '../../../../api/endpoints/jira';
import { JiraDateInput } from './JiraDateInput';
import { JiraLongStringInput } from './JiraLongStringInput';
import { JiraMultiUserOptionInput } from './JiraMultiUserOptionInput';
import { JiraNumberInput } from './JiraNumberInput';
import { JiraOptionInput } from './JiraOptionInput';
import { JiraOptionWithChildInput } from './JiraOptionWithChildInput';
import { JiraSprintInput } from './JiraSprintInput';
import { JiraStringInput } from './JiraStringInput';
import { JiraToggleOption, JiraTogglesInput } from './JiraTogglesInput';
import { JiraUserInput } from './JiraUserInput';

// Define the JiraOption interface for option fields
interface JiraOption {
    self: string;
    value: string;
    id: string;
}

export interface JiraFieldInputProps {
    field: JiraFieldData2;
    value: any;
    error?: boolean;
    onChange: (value: any) => void;
    onSubmit?: (value: any) => void;
}

// Field names that should use the long string input (textarea)
const LONG_STRING_FIELDS = [
    'description',
    'comment',
    'details',
    'notes',
];

export const JiraFieldInput = React.memo(({ field, value, error, onChange, onSubmit }: JiraFieldInputProps) => {
    onSubmit = onSubmit || (() => { });
    // Render the appropriate input component based on the field type
    switch (field.type) {
        case 'string':
            // Check if this field should use a long string input (textarea)
            const fieldNameLower = field.name.toLowerCase();
            const isLongStringField = LONG_STRING_FIELDS.some(term =>
                fieldNameLower.includes(term)
            );

            return isLongStringField
                ? <JiraLongStringInput {...{ field, value, error, onChange, onSubmit }} />
                : <JiraStringInput {...{ field, value, error, onChange, onSubmit }} />;

        case 'number':
            return <JiraNumberInput {...{ field, value, error, onChange, onSubmit }} />;

        case 'date':
            return <JiraDateInput {...{ field, value, error, onChange, onSubmit }} />;

        case 'option':
            return <JiraOptionInput
                field={field}
                value={value as JiraOption | null}
                error={error}
                onChange={(val) => onChange(val)}
                onSubmit={(val) => onSubmit(val)}
            />;

        case 'option-with-child':
            return <JiraOptionWithChildInput
                field={field}
                value={value}
                error={error}
                onChange={(val) => onChange(val)}
                onSubmit={(val) => onSubmit(val)}
            />;

        case 'user':
            return <JiraUserInput
                field={field}
                value={value as JiraUserLiteData | null}
                error={error}
                onChange={(val) => onChange(val)}
                onSubmit={(val) => onSubmit(val)}
            />;

        case 'multi-user':
            return <JiraMultiUserOptionInput
                field={field}
                value={value as JiraUserLiteData[] | null}
                error={error}
                onChange={(val) => onChange(val)}
                onSubmit={(val) => onSubmit(val)}
            />;

        case 'sprint':
            return <JiraSprintInput
                field={field}
                value={value as JiraSprintLiteData | null}
                error={error}
                onChange={(val) => onChange(val)}
                onSubmit={(val) => onSubmit(val)}
            />;

        case 'toggles':
            return <JiraTogglesInput
                field={field}
                value={value as JiraToggleOption[] | null}
                error={error}
                onChange={(val) => onChange(val)}
                onSubmit={(val) => onSubmit(val)}
            />;

        default:
            return null;
    }
});
